/**
 * Catalogue of feature tips, plus the rules for which ones are worth showing.
 *
 * Every tip is about a feature, and there are only two kinds:
 *
 *   Features you have    a reminder that it is there and what it does for you.
 *                        Plenty of practices are licensed for something, used it
 *                        once, and have not thought about it since.
 *
 *   Features you could   an explanation of what it would do, and a draft message
 *   have                 to us. Never a nag - the practice may have very good
 *                        reasons for not having it.
 *
 * Neither kind makes a claim about what the practice has or has not already set up,
 * so none of them can be wrong about it. A tip that says "you haven't configured
 * this" is only as good as the data behind it, and getting that wrong tells a paying
 * customer their product is broken when it is not.
 *
 * That means the only gate is licensing:
 *
 *   licensed(ctx)  cheap, synchronous, straight off the session. Does this feature
 *                  apply to this practice and user at all?
 *
 * There are deliberately no lookups here. Nothing is fetched, nothing is cached,
 * nothing is persisted. Tips are not suppressed because a practice configured the
 * feature once - plenty set something up, drift away from it, and are best served
 * by being reminded it exists.
 *
 * The licensed set comes back shuffled, and the card opens on whatever landed at the
 * front. A fixed order would mean the tips near the end of the list are only ever
 * seen by someone who clicks through, and with a catalogue this size that is most
 * of them. Reshuffling on every load also means switching practice, or coming back
 * tomorrow, is not the same tip again.
 *
 * A tip may also be marked `promoted`. Promoted tips share half the openings between
 * them, and the rest of the catalogue shares the other half, so a feature we are
 * pushing is what the card opens on roughly every second load. Only the opening tip
 * is weighted - the order behind it is the plain shuffle, so nothing is buried.
 *
 * The most a user can do is close the card, which lasts until they sign in again.
 *
 * Wording is plain and professional - what the feature does and what it saves,
 * addressed to the clinician. No exclamation marks, no marketing adjectives, and
 * nothing that reads as a scolding for not using something.
 *
 * cta is optional. A lot of features live inside a patient's record, and there is
 * nowhere sensible to send someone from the welcome page - the tip is worth knowing
 * on its own, and the card simply renders without a button. Where a tip does carry
 * one:
 *
 *   settings   deep-links a settings section. Carries the same licence gate the
 *              section itself does in settingTabsController, so it never opens on
 *              a section this practice does not get.
 *   payment    the practice wallet, for the features billed in tokens.
 *   aiAgent    the AI agent dialog.
 *   contact    a draft for the built-in Contact us dialog. {practice} is substituted
 *              before it opens and the user can edit it before sending. This is what
 *              the "could have" tips use - there is nowhere in the app to send
 *              someone for a feature they are not licensed for.
 */
app.factory("FeatureTips", [
	"$q",
	"Session",
	"countryConfig",
	"FEATURES",
	"REFERRALS_PATHWAY_1028",
	"PREMIUM_USER_STRIPE_FEATURE_1040",
	"SURGERY_WAITLIST_1049",
	"CLINIC_LETTER_282",
	"EORDERS_1054",
	"CALENDAR_AVAILABILITY_991",
	function (
		$q,
		Session,
		countryConfig,
		FEATURES,
		REFERRALS_PATHWAY_1028,
		PREMIUM_USER_STRIPE_FEATURE_1040,
		SURGERY_WAITLIST_1049,
		CLINIC_LETTER_282,
		EORDERS_1054,
		CALENDAR_AVAILABILITY_991
	) {
		const TIPS = [
			// ================================================================
			// Appointment reminders
			// ================================================================
			{
				id: "sms-reminders",
				icon: "fa-comment",
				title: "Patients can be texted before their appointment",
				body: "Enable text reminders on an appointment type and CatTrax sends them for you.",
				cta: { label: "Open reminder settings", action: "settings", section: "reminders" },
				licensed: (ctx) => !!ctx.practiceFeatures.appointmentSMSReminderFeature && ctx.hasCalendar,
			},
			{
				id: "email-reminders",
				icon: "fa-envelope",
				title: "Each appointment type can have its own reminder",
				body: "A theatre list and a routine review do not need the same message. Set the wording once per type and it is used every time.",
				cta: { label: "Open reminder settings", action: "settings", section: "reminders" },
				licensed: (ctx) => !!ctx.practiceFeatures.appointmentEmailReminderFeature && ctx.hasCalendar,
			},
			{
				id: "reminder-attachments",
				icon: "fa-paperclip",
				title: "Reminders can carry the paperwork with them",
				body: "Attach a consent form or questionnaire to an appointment type's reminder and every patient booked for it receives a copy in advance.",
				cta: { label: "Open reminder settings", action: "settings", section: "reminders" },
				licensed: (ctx) => !!ctx.practiceFeatures.appointmentEmailReminderFeature && ctx.hasCalendar,
			},
			{
				id: "reminder-merge-fields",
				icon: "fa-code",
				title: "Reminders are personalised for each patient",
				body: "Insert a merge field into your wording and every reminder goes out with that patient's own name, date and time.",
				cta: { label: "Open reminder settings", action: "settings", section: "reminders" },
				licensed: (ctx) => ctx.hasReminders && ctx.hasCalendar,
			},
			{
				id: "reminder-schedule",
				icon: "fa-clock-o",
				title: "You control when each reminder is sent",
				body: "Set how far ahead an appointment type's reminder goes out - a week before a theatre list, the day before a review.",
				cta: { label: "Open reminder settings", action: "settings", section: "reminders" },
				licensed: (ctx) => ctx.hasReminders && ctx.hasCalendar,
			},
			{
				id: "sms-billing",
				icon: "fa-credit-card",
				title: "Text reminders are billed to your practice wallet",
				body: "Set a minimum balance and a recharge amount and the wallet tops up automatically, so a low balance never quietly stops your reminders.",
				cta: { label: "Open payment settings", action: "payment" },
				licensed: (ctx) => !!ctx.practiceFeatures.appointmentSMSReminderFeature,
			},
			{
				id: "reminders-upsell",
				icon: "fa-bell",
				title: "CatTrax can remind patients about their appointments",
				body: "Reminders are sent automatically by email or text, in your wording and with your paperwork attached, so fewer patients miss an appointment.",
				cta: {
					label: "Ask us about reminders",
					action: "contact",
					message:
						"Hi CatTrax team,\n\n" +
						"We are interested in sending appointment reminders from CatTrax, by email and by text, " +
						"so patients hear from us before they arrive.\n\n" +
						"Please get in touch about setting this up for {practice}.",
				},
				licensed: (ctx) => !ctx.hasReminders,
			},

			// ================================================================
			// Calendar
			// ================================================================
			{
				id: "appointment-types",
				icon: "fa-calendar",
				title: "Your clinic day can be colour coded",
				body: "Give each appointment type its own colour and the calendar reads at a glance, for you and for whoever is booking.",
				cta: { label: "Open appointment types", action: "settings", section: "appointmentTypes" },
				licensed: (ctx) => !!ctx.practiceFeatures.calendarFeature,
			},
			{
				id: "appointment-default-length",
				icon: "fa-clock-o",
				title: "Each appointment type books at its own length",
				body: "Set a default duration per type and every booking starts the right size, from a short review through to a full theatre slot.",
				cta: { label: "Open appointment types", action: "settings", section: "appointmentTypes" },
				licensed: (ctx) => !!ctx.practiceFeatures.calendarFeature,
			},
			{
				id: "calendar-availability",
				icon: "fa-calendar-check-o",
				title: "The calendar knows when you are available",
				body: "Record the hours you are free or away, weekly through to every fourth week, and the days you do not work are no longer offered.",
				licensed: (ctx) => !!ctx.practiceFeatures.calendarFeature && ctx.flags.calendarAvailability,
			},
			{
				id: "calendar-upsell",
				icon: "fa-calendar",
				title: "CatTrax includes a clinic calendar",
				body: "Book patients against your own appointment types, colour code the day, and set the hours you are available to be booked.",
				cta: {
					label: "Ask us about the calendar",
					action: "contact",
					message:
						"Hi CatTrax team,\n\n" +
						"We are interested in using the CatTrax calendar to book our clinics, with our own " +
						"appointment types and availability.\n\n" +
						"Please get in touch about enabling this for {practice}.",
				},
				licensed: (ctx) => !ctx.practiceFeatures.calendarFeature,
			},

			// ================================================================
			// Tasks
			// ================================================================
			{
				id: "task-automation",
				icon: "fa-magic",
				title: "CatTrax can create your tasks for you",
				body: "Have a task created and assigned as soon as a referral, operation note or histology result is completed, so nothing waits to be noticed.",
				cta: { label: "Open task settings", action: "settings", section: "taskAutomation" },
				licensed: (ctx) => ctx.hasTasks,
			},
			{
				id: "task-labels",
				icon: "fa-tags",
				title: "Your tasks can be colour coded",
				body: "Create your own labels, each with its own colour, so a glance down the list shows what kind of work is waiting.",
				cta: { label: "Open task labels", action: "settings", section: "taskLabels" },
				licensed: (ctx) => ctx.hasTasks,
			},
			{
				id: "task-automation-premium-lens",
				icon: "fa-eye",
				title: "A lens selection can start the next step",
				body: "Have a task raised as soon as a premium lens patient selection form is completed, so ordering does not wait on a handover.",
				cta: { label: "Open task settings", action: "settings", section: "taskAutomation" },
				licensed: (ctx) => ctx.hasTasks,
			},
			{
				id: "tasks-upsell",
				icon: "fa-check-square-o",
				title: "CatTrax can manage your practice's to-do list",
				body: "Assign work to a colleague, label it by type, and have routine jobs created for you as notes and referrals are completed.",
				cta: {
					label: "Ask us about tasks",
					action: "contact",
					message:
						"Hi CatTrax team,\n\n" +
						"We are interested in using tasks in CatTrax, so work can be assigned within the practice " +
						"and created automatically as notes and referrals are completed.\n\n" +
						"Please get in touch about enabling this for {practice}.",
				},
				licensed: (ctx) => !ctx.hasTasks,
			},

			// ================================================================
			// Surgery waitlist
			// ================================================================
			{
				id: "surgery-waitlist",
				icon: "fa-hourglass-half",
				title: "Your surgery waitlist is in the top bar",
				body: "Everyone waiting for a date appears in one list, with a badge showing how many still need booking, across every practice you work in.",
				// The waitlist screen only lists practices with tasks within the practice,
				// so without that the icon opens on nothing.
				licensed: (ctx) => ctx.flags.surgeryWaitlist && !!ctx.practiceFeatures.sendTasksWithinPracticeFeature,
			},
			{
				id: "surgery-waitlist-tasks",
				icon: "fa-magic",
				title: "Joining the waitlist can raise its own task",
				body: "Add the surgery waitlist to your automation and the booking work is assigned the moment a patient goes on it.",
				cta: { label: "Open task settings", action: "settings", section: "taskAutomation" },
				licensed: (ctx) => ctx.hasTasks && ctx.flags.surgeryWaitlist,
			},

			// ================================================================
			// AI dictation
			// ================================================================
			{
				id: "ai-transcription",
				promoted: true,
				icon: "fa-microphone",
				title: "A free trial of AI dictation is available",
				body: "Dictate as you consult and have your notes written up for you. Your free trial begins the first time you record.",
				cta: { label: "See how it works", action: "payment" },
				licensed: (ctx) => !!ctx.countryFeatures.transcriptionFeatureEnabled,
			},
			{
				id: "ai-transcription-in-note",
				promoted: true,
				icon: "fa-microphone",
				title: "You can dictate straight into a progress note",
				body: "Record the consult from the patient's record and the written-up note comes back into the note you were already working on.",
				licensed: (ctx) =>
					!!ctx.countryFeatures.transcriptionFeatureEnabled && !!ctx.practiceFeatures.dateTranscriptionTrialStartedUtc,
			},
			{
				id: "transcription-prompts",
				promoted: true,
				icon: "fa-sliders",
				title: "You decide how your dictation is written up",
				body: "Save your own prompts for the way you structure notes, mark the ones you use most as favourites, and choose a prompt per recording.",
				licensed: (ctx) =>
					!!ctx.countryFeatures.transcriptionFeatureEnabled && !!ctx.userFeatures.editableSystemTranscriptionPromptsFeature,
			},
			// ================================================================
			// AI agents
			// ================================================================
			{
				id: "ai-agent-calls",
				icon: "fa-phone",
				title: "An AI agent can make the call for you",
				body: "Schedule a call for a date and time, choose the agent, and the call is made without anyone sitting on the phone.",
				cta: { label: "Open AI agent settings", action: "aiAgent" },
				licensed: (ctx) => !!ctx.practiceFeatures.elevenLabsAiAgentFeature,
			},
			{
				id: "ai-agent-prompts",
				icon: "fa-file-text-o",
				title: "Your agents work from prompts you write",
				body: "Prompt management keeps that wording in one place, so every call opens the same way whoever scheduled it.",
				cta: { label: "Open AI agent settings", action: "aiAgent" },
				licensed: (ctx) => !!ctx.practiceFeatures.elevenLabsAiAgentFeature,
			},
			{
				id: "ai-agent-knowledge",
				icon: "fa-book",
				title: "Your AI agent can work from your own documents",
				body: "Add your practice's material to the knowledge base and the agent answers from it rather than from general knowledge.",
				cta: { label: "Open AI agent settings", action: "aiAgent" },
				licensed: (ctx) => !!ctx.practiceFeatures.elevenLabsAiAgentFeature,
			},
			{
				id: "ai-agent-upsell",
				icon: "fa-headphones",
				title: "CatTrax can call patients on your behalf",
				body: "An AI agent, working from your prompt and your own documents, makes the routine calls and reports back what was said.",
				cta: {
					label: "Ask us about AI agents",
					action: "contact",
					message:
						"Hi CatTrax team,\n\n" +
						"We would like to hear more about the AI agents in CatTrax, and having routine calls to " +
						"patients made for us.\n\n" +
						"Please get in touch about enabling this for {practice}.",
				},
				licensed: (ctx) => !ctx.practiceFeatures.elevenLabsAiAgentFeature,
			},

			// ================================================================
			// SNOMED coding
			// ================================================================
			{
				id: "snomed-coding",
				icon: "fa-database",
				title: "Your notes are coded as you write them",
				body: "The clinical concepts in a note are identified and coded for you. Hover a highlighted term to see what it was matched to.",
				licensed: (ctx) => !!ctx.practiceFeatures.snomedAiExtractionFeature,
			},
			{
				id: "snomed-upsell",
				icon: "fa-database",
				title: "Your notes could be coded as you write them",
				body: "SNOMED AI extraction identifies the clinical concepts in a note and keeps them coded, without anyone stopping to look a term up.",
				cta: {
					label: "Ask us about SNOMED coding",
					action: "contact",
					message:
						"Hi CatTrax team,\n\n" +
						"We would like to hear more about SNOMED AI extraction, and having the clinical concepts in " +
						"our notes coded as they are written.\n\n" +
						"Please get in touch about enabling this for {practice}.",
				},
				licensed: (ctx) => !ctx.practiceFeatures.snomedAiExtractionFeature,
			},

			// ================================================================
			// Imaging and files
			// ================================================================
			{
				id: "dicom-viewer",
				icon: "fa-desktop",
				title: "Scans open without leaving the record",
				body: "DICOM studies on a patient's files open in the viewer in place, so there is no need to go back to the machine to review them.",
				licensed: (ctx) => !!ctx.practiceFeatures.dicomFeature,
			},
			{
				id: "dicom",
				icon: "fa-desktop",
				title: "You can connect your imaging devices",
				body: "Send worklists directly to your DICOM devices and have scans upload back into the patient record automatically.",
				cta: {
					label: "Ask us about DICOM",
					action: "contact",
					message:
						"Hi CatTrax team,\n\n" +
						"We would like to connect our DICOM imaging machines to CatTrax, so worklists are sent to " +
						"the devices and scans upload back into the patient record automatically.\n\n" +
						"Please get in touch about setting this up for {practice}.",
				},
				licensed: (ctx) => !ctx.practiceFeatures.dicomFeature,
			},
			{
				id: "file-ai-analysis",
				icon: "fa-cloud-upload",
				title: "Uploaded files can classify themselves",
				body: "Select AI analysis when you upload and CatTrax works out the file type and which eye it belongs to, rather than asking you.",
				licensed: () => true,
			},

			// ================================================================
			// Patient record
			// ================================================================
			{
				id: "patient-graphs",
				icon: "fa-line-chart",
				title: "The patient's history is already charted",
				body: "Acuity, refraction, IOP, axial length, K values and astigmatism are plotted over time for both eyes. Click a chart to open it.",
				licensed: () => true,
			},
			{
				id: "favourite-patients",
				icon: "fa-star-o",
				title: "Star the patients you return to often",
				body: "Favourites sit behind the patient search, so the people you are following do not need looking up each time.",
				licensed: () => true,
			},
			{
				id: "recent-patients",
				icon: "fa-history",
				title: "The patients you have just seen are one hover away",
				body: "Hover the patient search to see your recent patients listed by day, so returning to this morning's record is a single click.",
				licensed: (ctx) => !!ctx.userFeatures.recentPatientsFeature,
			},
			{
				id: "advanced-search",
				icon: "fa-search",
				title: "You can search on more than a surname",
				// The label differs by country and is full of acronyms, so only the leading
				// letter is dropped to lower case - "NHI" and "DVA" have to survive.
				body: (ctx) => {
					const label = ctx.countryFeatures.advancedSearchLabel;
					return (
						"Advanced search accepts " +
						label.charAt(0).toLowerCase() +
						label.slice(1) +
						", so a patient on the phone can be found from whatever detail they give you."
					);
				},
				licensed: () => true,
			},
			{
				id: "care-team-in-use",
				icon: "fa-users",
				title: "Correspondence follows the care team",
				body: "Record the GP, optometrist and specialists on a patient's care team once, and what you send reaches the right people afterwards.",
				licensed: () => true,
			},

			// ================================================================
			// Recording a visit
			// ================================================================
			{
				id: "medical-certificates",
				icon: "fa-file-text-o",
				title: "Medical certificates come out of the record",
				body: "Produce a certificate, or a return-to-work certificate, from the visit you have just recorded rather than writing it separately.",
				licensed: (ctx) => ctx.canRecordVisit,
			},
			{
				id: "injection-notes",
				icon: "fa-medkit",
				title: "Injections have a note of their own",
				body: "Record the medication, the eye, the location and the anaesthetic on a form built for it, so the detail is there next time.",
				licensed: (ctx) => ctx.canRecordVisit,
			},
			{
				id: "operation-note-procedures",
				icon: "fa-stethoscope",
				title: "Your operation notes remember your procedures",
				body: "A procedure you have typed in once can be saved and selected from the list next time, so a routine list writes up quickly.",
				licensed: (ctx) => ctx.canRecordSurgery,
			},
			{
				id: "premium-lens",
				icon: "fa-eye",
				title: "Premium lens selections have their own form",
				body: "The patient's selection is recorded on a dedicated form, with the options and pricing set out, and stays attached to the workup.",
				licensed: (ctx) => ctx.flags.premiumLens && ctx.isPrivatePractice,
			},
			{
				id: "eye-bank",
				icon: "fa-hospital-o",
				title: "Eye bank forms sit with the operation note",
				body: "Graft details are recorded alongside the operation they belong to, rather than on a separate form kept elsewhere.",
				licensed: (ctx) => ctx.canRecordSurgery,
			},
			{
				id: "surgery-agreement",
				icon: "fa-pencil-square-o",
				title: "Surgery agreements can be signed on screen",
				body: "The agreement is raised from the patient's record and signed there, so consent is never the thing holding a booking up.",
				licensed: (ctx) => ctx.canRecordSurgery,
			},

			// ================================================================
			// Correspondence and ordering
			// ================================================================
			{
				id: "clinic-letters",
				icon: "fa-envelope-o",
				title: "Your clinic letters are all in one place",
				body: "Letters for a clinic date are listed together and can be sent to your practice EDI as well as to the recipient.",
				licensed: (ctx) => ctx.flags.clinicLetter,
			},
			{
				id: "clinic-letter-templates",
				icon: "fa-clone",
				title: "A letter you write often can become a template",
				body: "Save the wording as a template and reuse it across every practice you work in, rather than rewriting it in each.",
				licensed: (ctx) => ctx.flags.clinicLetter,
			},
			{
				id: "eorders",
				icon: "fa-flask",
				title: "You can order labs from the patient's record",
				body: "Eclair eOrders opens from the patient header, already knowing who you are ordering for.",
				licensed: (ctx) => ctx.flags.eOrders && ctx.country === "NZ",
			},
			{
				id: "eorders-hpi",
				icon: "fa-id-card",
				title: "eOrders needs your HPI CPN",
				body: "Ordering is unlocked once the patient has an NHI and your profile holds your HPI CPN. It is worth adding yours once.",
				cta: { label: "Open your profile", action: "settings", section: "profile" },
				licensed: (ctx) => ctx.flags.eOrders && ctx.country === "NZ",
			},
			{
				id: "custom-pdf-headers-in-use",
				icon: "fa-file-pdf-o",
				title: "Your documents go out on your letterhead",
				body: "The PDFs CatTrax produces carry your practice's banner, so what reaches a patient or a colleague looks like it came from you.",
				licensed: (ctx) => !!ctx.practiceFeatures.customPdfHeadersFeature,
			},
			{
				id: "custom-pdf-headers",
				icon: "fa-file-pdf-o",
				title: "Your documents could carry your own letterhead",
				body: "Custom PDF headers put your practice's banner on everything CatTrax produces for you to send out.",
				cta: {
					label: "Ask us about PDF banners",
					action: "contact",
					message:
						"Hi CatTrax team,\n\n" +
						"We would like our own banner on the PDFs CatTrax produces, so documents we send out carry " +
						"our practice's letterhead.\n\n" +
						"Please get in touch about setting this up for {practice}.",
				},
				licensed: (ctx) => !ctx.practiceFeatures.customPdfHeadersFeature,
			},

			// ================================================================
			// Referrals
			// ================================================================
			{
				// Ophthalmologists, and optometrists who already have Premium User. These
				// can pick their referral types today, so send them straight to the list.
				id: "referral-types",
				icon: "fa-share",
				title: "Other clinicians can find you by referral type",
				body: "Choose the referral types you accept and you will be listed when colleagues refer a patient.",
				cta: { label: "Open your profile", action: "settings", section: "profile" },
				licensed: (ctx) => ctx.flags.referralsPathway && ctx.canListReferralTypes,
			},
			{
				// Optometrists without Premium User. The referral type list is not shown
				// to them at all, so pointing at it would be a dead end - the step that
				// actually unblocks them is the subscription.
				id: "premium-referrals",
				icon: "fa-star",
				title: "Premium Users can receive referrals",
				body: "Subscribe as a Premium User to be listed for the referral types you accept, so other clinicians can refer patients directly to you.",
				cta: { label: "See Premium User", action: "settings", section: "profile" },
				licensed: (ctx) =>
					ctx.flags.referralsPathway && ctx.flags.premiumUserStripe && ctx.isOptometrist && !ctx.isPremiumUser,
			},
			{
				id: "referral-files",
				icon: "fa-paperclip",
				title: "A referral can travel with its files",
				body: "Attach the scans and documents that belong with a referral and they arrive together, rather than being chased up afterwards.",
				licensed: (ctx) => ctx.flags.referralsPathway && ctx.countryFeatures.referralFeatureEnabled,
			},

			// ================================================================
			// Billing
			// ================================================================
			{
				id: "billing-in-record",
				icon: "fa-money",
				title: "You can invoice from the patient's record",
				body: "Raise the invoice where the work was recorded and the billing status stays alongside the notes, rather than in another system.",
				licensed: (ctx) => !!ctx.practiceFeatures.billingFeature,
			},
			{
				id: "xero",
				icon: "fa-exchange",
				title: "CatTrax works with Xero",
				body: "Push invoices directly into Xero and see each patient's billing status without leaving their record.",
				cta: {
					label: "Ask us about Xero",
					action: "contact",
					message:
						"Hi CatTrax team,\n\n" +
						"We would like to connect Xero to CatTrax, so invoices go straight into Xero and we can see " +
						"each patient's billing status from their record.\n\n" +
						"Please get in touch about setting this up for {practice}.",
				},
				licensed: (ctx) => !!ctx.practiceFeatures.billingFeature,
			},
			{
				id: "billing-upsell",
				icon: "fa-money",
				title: "You could be invoicing from the patient's record",
				body: "Raise the invoice where the work was recorded rather than rekeying it somewhere else, then push it through to Xero.",
				cta: {
					label: "Ask us about billing",
					action: "contact",
					message:
						"Hi CatTrax team,\n\n" +
						"We are interested in invoicing from CatTrax, so billing sits alongside the record of the " +
						"work rather than being rekeyed elsewhere.\n\n" +
						"Please get in touch about enabling this for {practice}.",
				},
				licensed: (ctx) => !ctx.practiceFeatures.billingFeature,
			},
			{
				id: "claimable-items",
				icon: "fa-list-alt",
				title: "Your item codes are ready to claim against",
				body: "Keep your practice's item codes and fees in CatTrax and select them straight onto a patient's claim.",
				licensed: (ctx) => !!ctx.countryFeatures.medicareFeatureEnabled,
			},

			// ================================================================
			// New Zealand
			// ================================================================
			{
				id: "nhi-search",
				icon: "fa-search",
				title: "You can look patients up on the NHI",
				body: "Search the National Health Index from the patient search and bring demographics straight in, rather than typing them out.",
				licensed: (ctx) =>
					ctx.country === "NZ" && !!(ctx.practiceFeatures.nhiFeature || ctx.practiceFeatures.fhirNhiFeature),
			},
			{
				id: "nhi-lookup",
				icon: "fa-search",
				title: "You could look patients up on the NHI",
				body: "Searching the National Health Index from CatTrax means a patient's demographics come from the index rather than from a form.",
				cta: {
					label: "Ask us about NHI",
					action: "contact",
					message:
						"Hi CatTrax team,\n\n" +
						"We would like to search the NHI from CatTrax, so patient demographics come straight from " +
						"the index rather than being typed in.\n\n" +
						"Please get in touch about enabling this for {practice}.",
				},
				licensed: (ctx) =>
					ctx.country === "NZ" && !ctx.practiceFeatures.nhiFeature && !ctx.practiceFeatures.fhirNhiFeature,
			},
			{
				id: "nzf-prescribing",
				icon: "fa-medkit",
				title: "You prescribe from the NZ Formulary",
				body: "New Zealand Formulary medicines sit behind the prescribing screens, so doses and forms come from the source rather than from memory.",
				licensed: (ctx) => ctx.country === "NZ",
			},
			{
				id: "e-prescriptions",
				icon: "fa-file-text-o",
				title: "Prescriptions can be sent electronically",
				body: "An e-prescription is raised from the patient's medications, so nothing needs printing and handing over to reach a pharmacy.",
				licensed: (ctx) => !!ctx.countryFeatures.ePrescriptionsFeatureEnabled,
			},
			{
				id: "progress-note-to-gp",
				icon: "fa-share-square-o",
				title: "Progress notes can be sent to the patient's GP",
				body: "A completed note can be sent on to the GP as you finish it, so the referrer hears back without a separate job later.",
				licensed: (ctx) => !!ctx.countryFeatures.sendProgressNoteToGpFeatureEnabled,
			},
		];

		// The only thing a user can switch off, and only until they sign in again.
		let sessionHidden = false;

		// Fisher-Yates. Shuffles a copy - TIPS itself is the catalogue and stays put.
		function shuffle(tips) {
			const out = tips.slice();
			for (let i = out.length - 1; i > 0; i--) {
				const j = Math.floor(Math.random() * (i + 1));
				const held = out[i];
				out[i] = out[j];
				out[j] = held;
			}
			return out;
		}

		// Share of loads that should open on a promoted tip.
		const PROMOTED_SHARE = 0.5;

		// Every licensed tip is otherwise as likely as any other to land at the front,
		// which is fine for a settled catalogue but not for a feature we are trying to
		// put in front of people. A tip marked `promoted` gets half the openings between
		// the promoted tips, leaving the other half to the rest of the catalogue.
		//
		// Only the opening tip is weighted. The order behind it stays a plain shuffle, so
		// nothing is buried and clicking through still reaches every tip exactly once.
		function promoteToFront(tips) {
			const wantPromoted = Math.random() < PROMOTED_SHARE;
			const index = firstIndexOf(tips, wantPromoted);
			// Whichever side the coin landed on may have nothing licensed behind it, and
			// it may already be at the front. Either way the shuffle stands as it is.
			if (index <= 0) {
				return tips;
			}
			const out = tips.slice();
			const held = out[0];
			out[0] = out[index];
			out[index] = held;
			return out;
		}

		function firstIndexOf(tips, promoted) {
			for (let i = 0; i < tips.length; i++) {
				if (!!tips[i].promoted === promoted) {
					return i;
				}
			}
			return -1;
		}

		// A tip whose wording depends on the session (the search label differs by
		// country) carries a function instead of a string. Resolved here so the card
		// only ever sees plain text.
		function render(tip, ctx) {
			if (typeof tip.body !== "function") {
				return tip;
			}
			return angular.extend({}, tip, { body: tip.body(ctx) });
		}

		function context() {
			const practice = Session.practice || null;
			const practiceFeatures = (practice && practice.practiceFeatures) || {};
			const user = Session.user || {};
			const userFeatures = user.features || {};
			const roles = user.roles || [];

			const isOphthalmologist = roles.indexOf("OPHTHALMOLOGIST") !== -1;
			const isOptometrist = roles.indexOf("OPTOMETRIST") !== -1;
			const isPremiumUser = !!userFeatures.premiumUserFeature;
			const practiceType = (practice && practice.type) || null;

			return {
				practice: practice,
				practiceFeatures: practiceFeatures,
				user: user,
				userFeatures: userFeatures,
				roles: roles,
				isOphthalmologist: isOphthalmologist,
				isOptometrist: isOptometrist,
				isPremiumUser: isPremiumUser,
				isPrivatePractice: practiceType === "PRIVATE",
				// Mirrors the gate on the referral types selector in the profile screen.
				canListReferralTypes: isOphthalmologist || (isOptometrist && isPremiumUser),
				// Mirrors recordVisitMenu: who gets the workup and injection note buttons,
				// and who gets operation notes on top of those.
				canRecordVisit: Session.canStartWorkup() || Session.hasPermission("CLINICAL_INJECTION_NOTE_WRITE"),
				canRecordSurgery: Session.hasPermission("SURGERY_OPERATION_NOTE_WRITE"),
				country: countryConfig.countryCode,
				// The whole country config, for the features that are switched on by which
				// site you signed in to rather than by a practice flag.
				countryFeatures: countryConfig,
				// The same groupings the settings rail and the nav use, so a tip never
				// points at a section or dialog this practice does not get.
				hasTasks: !!(practiceFeatures.sendTasksWithinPracticeFeature || practiceFeatures.sendTasksOutsidePracticeFeature),
				hasReminders: !!(practiceFeatures.appointmentEmailReminderFeature || practiceFeatures.appointmentSMSReminderFeature),
				// The reminder tips link into the reminder settings screen, and that screen
				// needs the calendar as well as a channel - reminders are written per
				// appointment type. Without it the CTA opens a section that is not there.
				hasCalendar: !!practiceFeatures.calendarFeature,
				flags: {
					referralsPathway: !!REFERRALS_PATHWAY_1028,
					premiumUserStripe: !!PREMIUM_USER_STRIPE_FEATURE_1040,
					surgeryWaitlist: !!SURGERY_WAITLIST_1049,
					clinicLetter: !!(CLINIC_LETTER_282 && countryConfig.clinicalLettersFeatureEnabled),
					eOrders: !!EORDERS_1054,
					calendarAvailability: !!CALENDAR_AVAILABILITY_991,
					premiumLens: !!(FEATURES && FEATURES.PREMIUMLENS),
				},
			};
		}

		return {
			/**
			 * Resolves to the tips worth showing right now, in a fresh random order, with a
			 * promoted tip at the front half the time.
			 * Requires a practice to be selected. Kept promise-based so callers do not
			 * care that it is synchronous.
			 */
			relevantTips: function () {
				const ctx = context();

				if (!ctx.practice) {
					return $q.when([]);
				}

				const licensed = [];

				TIPS.forEach(function (tip) {
					try {
						if (tip.licensed(ctx)) {
							licensed.push(render(tip, ctx));
						}
					} catch (e) {
						// A malformed session should never take the welcome page down.
					}
				});

				return $q.when(promoteToFront(shuffle(licensed)));
			},

			/** Closes the card until the next sign-in, never longer. */
			hideForSession: function () {
				sessionHidden = true;
			},

			isHiddenForSession: function () {
				return sessionHidden;
			},

			/** Test / support hook - reopens the card. */
			reset: function () {
				sessionHidden = false;
			},
		};
	},
]);
